import React, { useEffect, useState } from "react";
import PopUpWrapper from "@components/PopUpWrapper";
import { Suggestion } from "@components/SuggestionsInput";
import ActiveTaskRooms from "./ActiveTaskRooms";
import SelectIssue from "./SelectIssue";
import theme from "@styles/theme";
import { selectRoomToggles } from "@store/ui/uiSelectors";
import useAppSelector from "@hooks/useAppSelector";
import { selectRoomSpacesByIds } from "@store/space/spaceSelectors";
import { hidePopUp, resetRoomToggles, setRoomToggle } from "@store/ui/uiSlice";
import { RoomSpace } from "@store/space/types";
import useAppDispatch from "@hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import { RecurringIssue } from "@store/recurringIssues/types";
import {
  selectAllRecurringIssueIds,
  selectRecurringIssueEntities,
} from "@store/recurringIssues/recurringIssueSelectors";
import { IssueType } from "@store/issue/types";
import { fetchRecurringIssues } from "@store/recurringIssues/recurringIssueThunk";
import { createIssueBatch } from "@store/issue/issueThunk";

const AssignIssueForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const roomToggles = useAppSelector(selectRoomToggles);
  const selectedRoomIds = Object.keys(roomToggles);
  const issueEntities = useAppSelector(selectRecurringIssueEntities);

  const selectedSpaces = useAppSelector((state) =>
    selectRoomSpacesByIds(state, selectedRoomIds),
  );
  const issueIds = useAppSelector(selectAllRecurringIssueIds);

  const assignIssues = (issues: RecurringIssue[]) => {
    const issueAssignments = {
      recurringIssueIds: issues.map(issue => issue.id),
      spaceIds: selectedRoomIds
    };
    dispatch(createIssueBatch(issueAssignments));
    dispatch(resetRoomToggles());
  };

  const allSuggestions = issueIds.reduce(
    (suggestions: Suggestion[], issueId) => {
      const issue = issueEntities[issueId];
      if (issue === undefined) return suggestions;

      suggestions.push({
        label: issue.label,
        key: issue.id as string,
      });

      return suggestions;
    },
    [],
  );

  const [selectedIssues, setSelectedIssues] = useState<{
    [key: string]: RecurringIssue;
  }>({});
  const [suggestions, setSuggestions] = useState<Suggestion[]>(allSuggestions);
  const [selectedIssue, setSelectedIssue] = useState<RecurringIssue>();
  const [isValid, setIsValid] = useState(false);

  const removeSpace = (id: RoomSpace["id"]) => {
    dispatch(setRoomToggle(id));
  };

  const editIssue = (id: RecurringIssue["id"]) => {
    let newSelectedIssues = selectedIssues;

    if (newSelectedIssues && newSelectedIssues[id]) {
      delete newSelectedIssues[id];
    } else {
      const issue = issueEntities[id];
      if (!newSelectedIssues) newSelectedIssues = {};
      if (issue !== undefined) newSelectedIssues[id] = issue;
    }
    const generatedSuggestions = allSuggestions.filter(
      (suggestion) => !selectedIssues[suggestion.key],
    );

    setSuggestions(generatedSuggestions);

    setSelectedIssues(newSelectedIssues);
  };

  const updateIssue = (issue: RecurringIssue) => {
    const newSelectedIssues = selectedIssues;
    newSelectedIssues[issue.id] = issue;
    setSelectedIssues(newSelectedIssues);
    setSelectedIssue(newSelectedIssues[issue.id]);
  };

  const setIssueById = (id: RecurringIssue["id"] | null) => {
    if (id === null) {
      setSelectedIssue(undefined);
      return;
    }
    if (selectedIssues[id]) {
      setSelectedIssue(selectedIssues[id]);
      return;
    }
    setSelectedIssue(issueEntities[id]);
  };

  const onAssign = () => {
    assignIssues(Object.values(selectedIssues));
    dispatch(hidePopUp());
  };

  useEffect(() => {
    dispatch(fetchRecurringIssues({ issueType: IssueType.HOUSEKEEPING }));
  }, [dispatch]);

  return (
    <PopUpWrapper>
      <div className="assignTaskContainer">
        <h3>{t("tasks.assignTask")}</h3>
        <SelectIssue
          issues={selectedIssues}
          suggestions={suggestions}
          editIssues={editIssue}
          updateIssue={updateIssue}
          selectedIssue={selectedIssue}
          setSelectedIssue={setIssueById}
          setIsValid={setIsValid}
        />

        <ActiveTaskRooms
          spaces={selectedSpaces}
          showRooms={false}
          removeSpace={removeSpace}
        />

        <button
          className={isValid ? "" : "inactive"}
          type="submit"
          disabled={!isValid}
          onClick={onAssign}>
          {t("tasks.assign")}
        </button>
      </div>
      <style jsx>
        {`
          .assignTaskContainer {
            display: flex;
            flex-direction: column;
            width: 310px;
          }
          label {
            font-weight: 600;
            font-family: Source Sans Pro;
            font-size: 13px;
            display: block;
          }

          input,
          textarea {
            font-family: Source Sans Pro;
            font-style: normal;
            font-weight: normal;
            font-size: 17px;
            line-height: 20px;
            width: 229px;
            outline: none;
            border: none;
            border-bottom: solid 1px ${theme.colors.lightGray};
            resize: none;
          }
          .details {
            width: 100%;
            margin: 0px;
          }
          .roomsContainer {
            overflow-y: scroll;
          }

          button {
            align-self: flex-end;
            color: #ffffff;
            background: #ff8100;
            font-weight: bold;
            margin-top: 20px;
            font-size: 16px;
            line-height: 20px;
            border-radius: 4px;
            padding: 11px 22px;
            border: none;
            outline: none;
            cursor: pointer;
          }

          .inactive {
            cursor: not-allowed;
            opacity: 0.2;
          }
          button.addTask {
            margin-top: 40px;
          }
        `}
      </style>
    </PopUpWrapper>
  );
};

export default AssignIssueForm;
